import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { PLATFORMS, FORMATS, OBJECTIVES, STATUSES } from "@/lib/constants";
import { toast } from "sonner";

export default function NewPublicationModal({ open, setOpen, clients, defaultClientId, defaultDate, onCreated }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const f = {
    client_id: clients[0]?.id || "",
    date: defaultDate || new Date().toISOString().slice(0, 10),
    time: "18:00", platform: "instagram", format: "post",
    objective: "engagement", status: "a_creer", title: "",
    ...form,
  };
  if (defaultClientId) f.client_id = defaultClientId;
  const lockedClient = defaultClientId ? clients.find((c) => c.id === defaultClientId) : null;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (openDetail) => {
    if (!f.title.trim() || !f.client_id) return;
    setLoading(true);
    try {
      const { data } = await api.post("/publications", f);
      toast.success("Publication créée");
      setOpen(false); setForm({});
      onCreated?.(data);
      if (openDetail) navigate(`/publications/${data.id}`);
    } catch { toast.error("Erreur lors de la création"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="new-publication-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Nouvelle publication</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Client</Label>
            {lockedClient ? (
              <div className="mt-1.5 flex items-center gap-2 border border-neutral-200 rounded-md px-3 py-2 bg-neutral-50 text-sm font-medium" data-testid="np-client-locked">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ background: lockedClient.color || "#94A3B8" }}>{lockedClient.name.slice(0, 1)}</span>
                {lockedClient.name}
              </div>
            ) : (
              <Select value={f.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="np-client" className="mt-1.5"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="col-span-2">
            <Label>Titre</Label>
            <Input data-testid="np-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex : Quel soin visage choisir ?" className="mt-1.5" />
          </div>
          <div>
            <Label>Date</Label>
            <Input data-testid="np-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Heure</Label>
            <Input data-testid="np-time" type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Plateforme</Label>
            <Select value={f.platform} onValueChange={(v) => set("platform", v)}>
              <SelectTrigger data-testid="np-platform" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Format</Label>
            <Select value={f.format} onValueChange={(v) => set("format", v)}>
              <SelectTrigger data-testid="np-format" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{FORMATS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Objectif</Label>
            <Select value={f.objective} onValueChange={(v) => set("objective", v)}>
              <SelectTrigger data-testid="np-objective" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{OBJECTIVES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Statut</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="np-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button data-testid="np-submit" onClick={() => submit(false)} disabled={loading || !f.title.trim()}
            variant="outline" className="flex-1">Créer</Button>
          <Button data-testid="np-submit-open" onClick={() => submit(true)} disabled={loading || !f.title.trim()}
            className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white">Créer et ouvrir</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
